import { useState } from 'react';
import { Info, RotateCcw, Trash2, Undo2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import type { SalesOrderLineItemDto } from '../types/salesOrder.types';

// Forked from quotation's LineItemInfoPopover. Per-row info popup that shows
// the matched NetSuite item, any notes, and quick actions (Revert / Delete /
// Re-match). Re-match is the SO-specific addition since quotation's table
// already pipes the re-match action through the toolbar instead.

interface Props {
  item: SalesOrderLineItemDto;
  onRevert?: () => void;
  onDelete?: () => void;
  onRematch?: () => void;
  /** When set, the Re-match action is shown disabled with this reason as a tooltip.
   *  86d3dxnd7: the spec is invalid for re-match (bad grade / missing diameter or
   *  length), so calling the endpoint would 400 — guide instead of firing. */
  rematchDisabledReason?: string | null;
}

export function SalesOrderLineItemInfoPopover({
  item,
  onRevert,
  onDelete,
  onRematch,
  rematchDisabledReason,
}: Props) {
  const [open, setOpen] = useState(false);
  const hasMatched = !!item.matchedSalesDescription?.trim();
  const hasNotes = !!item.notes?.trim();
  const canRevert = !!onRevert && !!item.originalSpecJson && item.isEdited;
  const canDelete = !!onDelete;
  const canRematch = !!onRematch;

  const show = hasMatched || hasNotes || canRevert || canDelete || canRematch;
  if (!show) return null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 text-muted-foreground hover:text-foreground"
          onClick={(e) => e.stopPropagation()}
          tabIndex={-1}
          title="Line details"
        >
          <Info className="h-4 w-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96">
        <div className="space-y-3">
          {hasMatched && (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Matched NetSuite item
              </p>
              <p className="mt-1 text-sm font-medium">{item.matchedSalesDescription}</p>
              {item.matchedItemId && (
                <p className="mt-0.5 text-[11px] font-mono text-muted-foreground">
                  item #{item.matchedItemId}
                </p>
              )}
            </div>
          )}

          {hasNotes && (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Notes
              </p>
              <p className="mt-1 whitespace-pre-wrap text-sm text-foreground">{item.notes}</p>
            </div>
          )}

          {item.rawDescription && item.rawDescription !== item.matchedSalesDescription && (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Original (raw)
              </p>
              <p className="mt-1 text-sm italic text-muted-foreground">{item.rawDescription}</p>
            </div>
          )}

          {(canRevert || canDelete || canRematch) && (
            <div className="flex flex-wrap gap-2 border-t pt-3">
              {canRematch &&
                (rematchDisabledReason ? (
                  // Disabled wrapper carries the title — a disabled button doesn't
                  // fire hover events, so the tooltip must sit on the span.
                  <span title={rematchDisabledReason} className="inline-flex">
                    <Button type="button" variant="outline" size="sm" disabled>
                      <RotateCcw className="mr-1.5 h-3.5 w-3.5" />
                      Re-match
                    </Button>
                  </span>
                ) : (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      onRematch!();
                      setOpen(false);
                    }}
                  >
                    <RotateCcw className="mr-1.5 h-3.5 w-3.5" />
                    Re-match
                  </Button>
                ))}
              {canRevert && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    onRevert!();
                    setOpen(false);
                  }}
                >
                  <Undo2 className="mr-1.5 h-3.5 w-3.5" />
                  Revert
                </Button>
              )}
              {canDelete && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="text-destructive hover:text-destructive"
                  onClick={() => {
                    onDelete!();
                    setOpen(false);
                  }}
                >
                  <Trash2 className="mr-1.5 h-3.5 w-3.5" />
                  Delete
                </Button>
              )}
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
